import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, ArrowRight, FileText, Plus, Shield, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import {
  useEvidenceLinksBySourceQuery,
  type EvidenceLinkBySourceItem,
  type EvidenceSourceType,
} from '@/lib/api/evidence-links';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';

/**
 * T-0116 / E04-F03 — Evidence-link graph modal (`data-open-evidence`).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-round4a.js`
 * → `window.Modals.openEvidenceGraph`. A focal "source" node (a register
 * entry or RFI) sits in the center; cited evidence (target documents) radiate
 * out in a radial layout connected by curved edges. Click any node → opens
 * the citation source modal for that link.
 *
 * Direction tabs:
 *   - Outbound ("Cites …")   — what this entry cites; backed by
 *     `GET /api/v1/engagements/:id/evidence-links?source_type=&source_id=`.
 *   - Inbound  ("Cited by …") — what cites this entry; the v1 backend has
 *     no direct "inbound by source" composite endpoint (target-side reads
 *     are document-keyed), so the inbound tab surfaces an empty state with
 *     a follow-up callout. Flagged as a backend gap in the ticket return
 *     report.
 *
 * Why SVG and not a graph library: the spec recommends a lightweight SVG
 * for ≤50 nodes (ticket §6 "Risks and unknowns"). Cytoscape / reaflow add
 * 200-400KB of bundle for a feature whose typical n is 4-8 satellites.
 * If graphs grow we can swap to a library behind the same prop surface.
 *
 * RLS: the read endpoints all carry the engagement id in the URL; the
 * backend enforces engagement-scoped RLS per ADR-008. The modal opens
 * against firm-team auth — it is not a viewer-eligible surface.
 */

const RADIAL_W = 720;
const RADIAL_H = 460;
const SATELLITE_RADIUS = 175;
const FOCAL_HALF_W = 110;
const FOCAL_HALF_H = 36;
const SATELLITE_HALF_W = 95;
const SATELLITE_HALF_H = 30;

type Direction = 'outbound' | 'inbound';

export interface EvidenceGraphFocal {
  /** The graph centerpiece — a register entry or RFI. */
  source_type: EvidenceSourceType;
  source_id: string;
  /** Short label shown on the focal node (e.g. "R-018"). */
  display_id: string;
  /** Long-form title for the focal node + header. */
  title: string;
  /** Optional subtitle line (status / severity / etc.). */
  subtitle?: string;
}

export interface EvidenceGraphModalProps {
  engagementId: string;
  focal: EvidenceGraphFocal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Click-handler for a satellite node. The modal calls this with the
   * citation id + document title + locator so the host can open the
   * citation source modal (round-trip pattern).
   */
  onOpenCitation?: (link: EvidenceLinkBySourceItem) => void;
}

export function EvidenceGraphModal({
  engagementId,
  focal,
  open,
  onOpenChange,
  onOpenCitation,
}: EvidenceGraphModalProps) {
  const [direction, setDirection] = React.useState<Direction>('outbound');

  // Reset direction whenever the modal opens for a new focal — keeps the
  // outbound tab as the default entry point on every fresh open.
  React.useEffect(() => {
    if (open) setDirection('outbound');
  }, [open, focal?.source_id]);

  const enabled = open && Boolean(focal);
  const outboundQuery = useEvidenceLinksBySourceQuery(
    enabled ? engagementId : undefined,
    enabled ? focal?.source_type : undefined,
    enabled ? focal?.source_id : undefined,
  );

  const titleId = focal ? `ev-graph-title-${focal.source_id}` : 'ev-graph-title';
  const nodes: EvidenceLinkBySourceItem[] = outboundQuery.data?.items ?? [];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(1080px,96vw)] max-h-[92vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <Header focal={focal} titleId={titleId} onClose={() => onOpenChange(false)} />
          <Tabs direction={direction} onChange={setDirection} outboundCount={nodes.length} />
          <div className="grid min-h-0 grid-cols-1 overflow-hidden lg:grid-cols-[1fr_300px]">
            <Canvas
              focal={focal}
              nodes={nodes}
              direction={direction}
              loading={outboundQuery.isLoading}
              error={outboundQuery.isError}
              onNodeClick={(link) => onOpenCitation?.(link)}
            />
            <SidePanel
              focal={focal}
              nodes={nodes}
              direction={direction}
              loading={outboundQuery.isLoading}
              onNodeClick={(link) => onOpenCitation?.(link)}
            />
          </div>
          <Footer focal={focal} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Header({
  focal,
  titleId,
  onClose,
}: {
  focal: EvidenceGraphFocal | null;
  titleId: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-6 py-4">
      <div className="min-w-0">
        <p className="text-meta uppercase tracking-wide text-ink-500">Evidence graph</p>
        <Dialog.Title id={titleId} className="mt-1 truncate text-h2 font-semibold text-ink-900">
          <span className="mr-3 font-mono text-meta text-ink-500">{focal?.display_id ?? '—'}</span>
          {focal?.title ?? 'Loading…'}
        </Dialog.Title>
        {focal?.subtitle ? <p className="mt-1 text-meta text-ink-500">{focal.subtitle}</p> : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2.5',
            'text-meta font-medium text-ink-700 disabled:cursor-not-allowed disabled:opacity-50',
          )}
          title="Add evidence — wired in a future ticket"
        >
          <Plus className="h-3 w-3" aria-hidden="true" />
          Add evidence
        </button>
        <Dialog.Close
          aria-label="Close evidence graph"
          onClick={onClose}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-card',
            'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
          )}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Dialog.Close>
      </div>
    </div>
  );
}

function Tabs({
  direction,
  onChange,
  outboundCount,
}: {
  direction: Direction;
  onChange: (d: Direction) => void;
  outboundCount: number;
}) {
  const Tab = ({
    value,
    label,
    count,
    icon: Icon,
  }: {
    value: Direction;
    label: string;
    count: number | string;
    icon: typeof ArrowRight;
  }) => {
    const active = direction === value;
    return (
      <button
        type="button"
        onClick={() => onChange(value)}
        className={cn(
          'inline-flex h-9 items-center gap-2 border-b-2 px-3 text-body-sm font-medium',
          'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
          active
            ? 'border-avero-orange text-ink-900'
            : 'border-transparent text-ink-500 hover:text-ink-700',
        )}
        role="tab"
        aria-selected={active}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
        <span
          className={cn(
            'ml-1 inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-full px-1.5',
            'font-mono text-[10px]',
            active ? 'bg-ink-900 text-paper-0' : 'bg-ink-100 text-ink-700',
          )}
        >
          {count}
        </span>
      </button>
    );
  };
  return (
    <div className="flex items-center gap-2 border-b border-ink-100 bg-paper-0 px-4" role="tablist">
      <Tab value="outbound" label="Cites" count={outboundCount} icon={ArrowRight} />
      <Tab value="inbound" label="Cited by" count="—" icon={ArrowLeft} />
      <p className="ml-auto py-2 text-meta text-ink-500">Click a node to open the source passage</p>
    </div>
  );
}

interface NodePos {
  link: EvidenceLinkBySourceItem;
  x: number;
  y: number;
}

/**
 * Compute satellite positions in a radial layout. With n ≤ 8 nodes we
 * stagger radius by ±10px on every-other position so labels don't
 * collide at the corners (matches the prototype's `(i % 2) ? +14 : -10`
 * trick).
 */
function radialLayout(n: number): Array<{ x: number; y: number }> {
  if (n === 0) return [];
  const cx = RADIAL_W / 2;
  const cy = RADIAL_H / 2;
  return Array.from({ length: n }, (_, i) => {
    const angle = -Math.PI / 2 + (i / n) * 2 * Math.PI;
    const rr = SATELLITE_RADIUS + (i % 2 ? 14 : -10);
    return { x: cx + Math.cos(angle) * rr, y: cy + Math.sin(angle) * rr };
  });
}

function Canvas({
  focal,
  nodes,
  direction,
  loading,
  error,
  onNodeClick,
}: {
  focal: EvidenceGraphFocal | null;
  nodes: ReadonlyArray<EvidenceLinkBySourceItem>;
  direction: Direction;
  loading: boolean;
  error: boolean;
  onNodeClick: (link: EvidenceLinkBySourceItem) => void;
}) {
  const positions: NodePos[] = React.useMemo(() => {
    const layout = radialLayout(nodes.length);
    return nodes.map((link, i) => ({
      link,
      x: layout[i]!.x,
      y: layout[i]!.y,
    }));
  }, [nodes]);

  const cx = RADIAL_W / 2;
  const cy = RADIAL_H / 2;

  return (
    <section className="min-w-0 overflow-auto bg-paper-100 p-6">
      <div className="mx-auto" style={{ width: RADIAL_W, height: RADIAL_H, position: 'relative' }}>
        {/* SVG edges underneath */}
        <svg
          width={RADIAL_W}
          height={RADIAL_H}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
        >
          {positions.map((p) => (
            <path
              key={p.link.id}
              d={`M ${cx} ${cy} L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`}
              stroke="var(--ink-300)"
              strokeWidth={1.5}
              strokeOpacity={0.55}
              fill="none"
            />
          ))}
        </svg>

        {/* Focal node */}
        <div
          className={cn(
            'absolute flex items-start gap-2 rounded-card border border-ink-900 bg-ink-900 px-3 py-2',
            'shadow-md',
          )}
          style={{
            left: cx - FOCAL_HALF_W,
            top: cy - FOCAL_HALF_H,
            width: FOCAL_HALF_W * 2,
          }}
        >
          <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center text-paper-0">
            <Shield className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-paper-200">
              {focal ? `${sourceTypeLabel(focal.source_type)} · FOCAL` : 'FOCAL'}
            </p>
            <p className="truncate text-body-sm font-semibold text-paper-0" title={focal?.title}>
              {focal?.title ?? '—'}
            </p>
            <p className="font-mono text-[10px] text-paper-200">{focal?.display_id ?? ''}</p>
          </div>
        </div>

        {/* Satellite nodes */}
        {positions.map((p) => (
          <button
            key={p.link.id}
            type="button"
            onClick={() => onNodeClick(p.link)}
            className={cn(
              'absolute flex items-start gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2.5 py-1.5 text-left',
              'shadow-sm transition-colors hover:border-avero-orange hover:bg-accent-amber-50',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
            style={{
              left: p.x - SATELLITE_HALF_W,
              top: p.y - SATELLITE_HALF_H,
              width: SATELLITE_HALF_W * 2,
            }}
            title={`${p.link.document_title} ${locatorLabel(p.link)}`}
          >
            <FileText className="mt-0.5 h-3 w-3 flex-none text-ink-500" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
                {p.link.target_kind === 'excel_cell' ? 'CELL' : 'DOCUMENT'}
              </p>
              <p className="truncate text-meta font-medium text-ink-900">{p.link.document_title}</p>
              <p className="truncate font-mono text-[10px] text-ink-500">{locatorLabel(p.link)}</p>
            </div>
          </button>
        ))}

        {/* Overlays */}
        {direction === 'outbound' && loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-paper-100/80">
            <Spinner />
          </div>
        ) : null}

        {direction === 'outbound' && !loading && nodes.length === 0 && !error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-sm rounded-card border border-dashed border-ink-200 bg-paper-0 px-6 py-5 text-center">
              <h3 className="text-h3 font-semibold text-ink-900">No outbound evidence yet</h3>
              <p className="mt-1 text-meta text-ink-500">
                This entry has no cited documents on file. Use &quot;Add evidence&quot; to anchor it
                against a document, RFI, or interview passage.
              </p>
            </div>
          </div>
        ) : null}

        {direction === 'outbound' && error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-sm rounded-card border border-dashed border-cite-broken bg-paper-0 px-6 py-5 text-center">
              <h3 className="text-h3 font-semibold text-cite-broken">Could not load graph</h3>
              <p className="mt-1 text-meta text-ink-500">
                The evidence-link endpoint returned an error. Retry from the close + reopen menu;
                cross-engagement IDs surface as not-found.
              </p>
            </div>
          </div>
        ) : null}

        {direction === 'inbound' ? <InboundPlaceholder /> : null}
      </div>
    </section>
  );
}

function InboundPlaceholder() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="max-w-md rounded-card border border-dashed border-ink-200 bg-paper-0 px-6 py-5 text-center">
        <h3 className="text-h3 font-semibold text-ink-900">Inbound graph — backend gap</h3>
        <p className="mt-1 text-meta text-ink-500 leading-relaxed">
          The v1 evidence-link API exposes target-side reads keyed on a document (page / cell), not
          a source. Surfacing &quot;what cites this entry&quot; needs a composite endpoint that
          inverts the join — flagged as a follow-up.
        </p>
      </div>
    </div>
  );
}

function SidePanel({
  focal,
  nodes,
  direction,
  loading,
  onNodeClick,
}: {
  focal: EvidenceGraphFocal | null;
  nodes: ReadonlyArray<EvidenceLinkBySourceItem>;
  direction: Direction;
  loading: boolean;
  onNodeClick: (link: EvidenceLinkBySourceItem) => void;
}) {
  return (
    <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto border-t border-ink-100 bg-paper-50 p-5 lg:border-l lg:border-t-0">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-700">
          {direction === 'outbound' ? `Outbound evidence · ${nodes.length}` : 'Inbound citations'}
        </p>
        <p className="mt-1 text-meta leading-[18px] text-ink-500">
          {direction === 'outbound'
            ? `Source records this ${focal ? sourceTypeLabel(focal.source_type).toLowerCase() : 'entry'} cites. Each link is a first-class evidence-link object.`
            : 'Other records that cite this entry. Backend follow-up — see canvas.'}
        </p>
      </div>
      {direction === 'outbound' && loading ? (
        <p className="text-meta text-ink-500">Loading…</p>
      ) : null}
      {direction === 'outbound'
        ? nodes.map((link) => (
            <button
              key={link.id}
              type="button"
              onClick={() => onNodeClick(link)}
              className={cn(
                'group flex flex-col items-start gap-1 rounded-card border border-ink-100 bg-paper-0 p-3 text-left',
                'transition-colors hover:border-avero-orange hover:bg-accent-amber-50',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
              )}
            >
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
                {link.target_kind === 'excel_cell' ? 'CELL' : 'DOCUMENT'}
              </span>
              <span className="text-body-sm font-medium text-ink-900 group-hover:text-ink-900">
                {link.document_title}
              </span>
              <span className="font-mono text-meta text-ink-500">{locatorLabel(link)}</span>
              <span className="text-[10px] text-ink-500">Linked {formatDate(link.created_at)}</span>
            </button>
          ))
        : null}
      <hr className="border-ink-100" />
      <p className="text-meta leading-[18px] text-ink-500">
        <strong className="text-ink-700">Audit defense:</strong> every link carries actor,
        timestamp, granularity, and target. The graph is the same shape any reviewer sees on
        re-open. Snapshot capture freezes a projection of these edges.
      </p>
    </aside>
  );
}

function Footer({ focal }: { focal: EvidenceGraphFocal | null }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
      <p className="text-meta text-ink-500">
        Source <span className="font-mono">{focal?.source_id ?? '—'}</span>
      </p>
      <Dialog.Close
        className={cn(
          'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
          'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
        )}
      >
        Close
      </Dialog.Close>
    </div>
  );
}

function sourceTypeLabel(t: EvidenceSourceType): string {
  return t === 'register_entry' ? 'REGISTER' : 'RFI';
}

function locatorLabel(link: EvidenceLinkBySourceItem): string {
  if (link.target_kind === 'excel_cell') {
    const sheet = link.sheet_name ?? '?';
    const cell = link.cell_address ?? '?';
    return `${sheet}!${cell}`;
  }
  const parts: string[] = [];
  if (link.section_path) parts.push(`§${link.section_path}`);
  if (typeof link.page === 'number') parts.push(`p.${link.page}`);
  return parts.length ? parts.join(' · ') : 'page passage';
}
